import type { LegalReviewStatus, LegalStatus, ActionBucket, SpendCategory } from "@/types/renewals";

import type { RenewalSort } from "./renewal-filters";

export const SPEND_CATEGORY_LABELS: Record<SpendCategory, string> = {
  p_cards: "P-Cards (10K)",
  transactional: "Transactional (10k - 50k)",
  core: "Core (50k - 150k)",
  strategic: "Strategic (>150K)",
};

export const LEGAL_REVIEW_LABELS: Record<LegalReviewStatus, string> = {
  not_yet_required: "Not yet required",
  required_not_sent: "Required - not sent",
  with_legal: "With Legal",
  complete: "Complete",
};

export const LEGAL_STATUS_LABELS: Record<LegalStatus, string> = {
  not_started: "Not started",
  under_review: "Under review",
  awaiting_information: "Awaiting information",
  awaiting_counterparty: "Awaiting counterparty",
  ready_for_signature: "Ready for signature",
  complete: "Complete",
  on_hold: "On hold",
};

// The computed renewal/termination date: the export column and the type of events created from it.
export const ACTION_DATE_LABEL = "Action Date";

export const ACTION_BUCKET_LABELS: Record<ActionBucket, string> = {
  overdue: "Overdue",
  within_30: "Due within 30 days",
  days_31_60: "31-60 days",
  days_61_90: "61-90 days",
  days_91_180: "90-180 days",
  over_180: "More than 180 days",
  unknown: "Action date unknown",
};

// Columns of the board, most urgent first; the other buckets are summary tiles.
export const BOARD_COLUMNS: Array<{ bucket: ActionBucket; hint: string; tone: string }> = [
  { bucket: "overdue", hint: "Notice window missed", tone: "border-red-200 bg-red-50 text-red-800" },
  { bucket: "within_30", hint: "Act now", tone: "border-orange-200 bg-orange-50 text-orange-800" },
  { bucket: "days_31_60", hint: "Prepare", tone: "border-amber-200 bg-amber-50 text-amber-800" },
  { bucket: "days_61_90", hint: "On the radar", tone: "border-sky-200 bg-sky-50 text-sky-800" },
  { bucket: "days_91_180", hint: "Watching", tone: "border-emerald-200 bg-emerald-50 text-emerald-800" },
];

export const BOARD_TILES: ActionBucket[] = ["over_180", "unknown"];

export const SORT_LABELS: Record<RenewalSort, string> = {
  notice: "Soonest notice",
  notice_desc: "Latest notice",
  term_end: "Soonest term end",
  value: "Highest value",
  supplier: "Supplier name",
};

// Statuses the Renewal Centre lists (the API returns no others on the board).
export const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  expiring: "Expiring",
  expired: "Expired",
};

// The detail page can open any readable family, so an unlisted status shows as stored.
export const statusLabel = (status: string) => STATUS_LABELS[status] ?? status;

// Match the manually assigned Contract Family status colours in Supplier Centre.
export const statusVariant = (status: string) => {
  if (status === "active") return "traffic-green";
  if (status === "expiring") return "traffic-amber";
  if (status === "expired" || status === "archived") return "traffic-red";
  return "secondary";
};

export const SELECT_CLASS =
  "h-10 max-w-full rounded-md border border-[#ddd5cb] bg-white pl-3 pr-10 text-sm text-[#111b35] focus:outline-none focus:ring-2 focus:ring-desert-sand-500 disabled:bg-gray-50 disabled:text-gray-500";
